import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { makeRequest } from "../../utils/request";

export type PermissionTier = "member" | "admin" | "owner";

export type PermissionEntry = {
  key: string;
  group: string;
  description: string;
  tier: PermissionTier;
  /**
   * Whether the current caller can grant this permission. Mirrors the
   * bounded-by-creator check on the API
   * (`OrganizationServiceAccountController.validateRequestedPermissions`):
   * the caller can only grant permissions they themselves hold; super-admin
   * bypasses. Used to disable entries in the picker the caller can't grant.
   */
  assignableByCaller: boolean;
  /** True for legacy permissions kept for backward compatibility. */
  deprecated?: boolean;
};

type State = {
  entries: PermissionEntry[];
  isLoading: boolean;
  loaded: boolean;
};

const initialState: State = {
  entries: [],
  isLoading: false,
  loaded: false,
};

const fetchPermissionRegistry = createAsyncThunk("permissionRegistry/fetch", async () => {
  const { data } = await makeRequest<{ data: PermissionEntry[] }>("/v1/permissions");
  return data;
});

const slice = createSlice({
  name: "permissionRegistry",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPermissionRegistry.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchPermissionRegistry.fulfilled, (state, action) => {
        state.entries = action.payload;
        state.isLoading = false;
        state.loaded = true;
      })
      .addCase(fetchPermissionRegistry.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

export const permissionRegistryActions = {
  ...slice.actions,
  fetch: fetchPermissionRegistry,
};
export const permissionRegistryReducer = slice.reducer;
